import React, { useState, useMemo, useEffect } from "react";
import { sortBy } from "lodash";
import { Form, useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useQuery } from "@apollo/client/react";
import {
  WAREHOUSE_NAMES,
  FETCH_PROJECT_QUERY,
  PROJECTS_DATA_QUERY,
  BILLING_PERIODS_QUERY,
} from "../../constants/graphql/queries";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Divider from "@mui/material/Divider";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import FilterNav from "../../layouts/ProjectLayout/FilterNav";
import FilterProject from "../../components/FilterProject";
import AddOptionalFiltersButtonForReceiving from "./AddOptionalFiltersButtonForReceiving";
import AssetFieldFiltersDisplay from "./AssetFieldFiltersDisplay";
import ProjectAutocomplete from "../../components/form/projects/ProjectAutocomplete";
import DateFilters from "../../components/report/DateFilters";
import SelectBillingPeriod from "./SelectBillingPeriod";

export default function SelectReceivingModalForm({
  name,
  buttonLabel,
  description,
  showReceivingNotes = true,
  alwaysShowDates = false,
  simpleValidation = false,
  reportType,
  inventoryAsOfDate = false,
  reportMode,
  warehouseDropdownEnabled,
  isPredefined = false,
}) {
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [showWarehouseFilters, setShowWarehouseFilters] = useState(false);
  const [showCostCenterFilters, setShowCostCenterFilters] = useState(false);
  const [showAssetFieldFilters, setShowAssetFieldFilters] = useState(false);
  const [shouldFetchFiltersData, setShouldFetchFiltersData] = useState(false);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState(null);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [isInventoryAsOfDateOpen, setIsInventoryAsOfDateOpen] = useState(false);

  const { values, setFieldValue, isSubmitting } = useFormikContext();
  const isOperationalActivityMode =
    reportMode === "operational_activity" ||
    reportType === "multi_project_billing_operational_activity";
  const isMultiProjectBillingReport = [
    "multi_project_billing",
    "multi_project_billing_roll_up_only",
    "multi_project_billing_operational_activity",
  ].includes(reportType);
  const disableProjectSelectAll = name === "Billing Report (Validation)";
  const hideOptionalFiltersButtonForTemplate = [
    "multi_project_billing_roll_up_only",
    "multi_project_billing_operational_activity",
  ].includes(reportType);

  const isWarehouseLocationsReport = reportType === "warehouse_locations";

  const shouldFetchAllWarehouses =
    (isWarehouseLocationsReport || values.projectId === "all" || values.projectIds === "all") &&
    shouldFetchFiltersData;
  const shouldFetchProjectWarehouses =
    values.projectId &&
    values.projectId !== "all" &&
    (!values.projectIds || values.projectIds === "all" || values.projectIds.length === 0) &&
    shouldFetchFiltersData;
  const shouldFetchMultiProjectData =
    values.projectIds &&
    Array.isArray(values.projectIds) &&
    values.projectIds.length > 0 &&
    shouldFetchFiltersData;

  const { data: { warehouses: allWarehouses = [] } = {}, loading: loadingAllWarehouses } = useQuery(
    WAREHOUSE_NAMES,
    {
      variables: { userWarehouses: true },
      skip: !shouldFetchAllWarehouses,
    }
  );

  const { data: { project: projectData } = {}, loading: loadingProjectData } = useQuery(
    FETCH_PROJECT_QUERY,
    {
      variables: { id: values.projectId },
      skip: !shouldFetchProjectWarehouses,
    }
  );

  const { data: { projectsData } = {}, loading: loadingProjectsData } = useQuery(
    PROJECTS_DATA_QUERY,
    {
      variables: { projectIds: values.projectIds || [] },
      skip: !shouldFetchMultiProjectData,
    }
  );

  const { data: billingData, loading: billingLoading } = useQuery(BILLING_PERIODS_QUERY, {
    variables: { projectId: values.projectId },
    skip: !values.projectId || values.projectId === "all",
    fetchPolicy: "network-only",
  });

  const billingPeriods = billingData?.project?.billingPeriods || [];
  const hasBillingPeriods = billingPeriods.length > 0;

  useEffect(() => {
    setIsCustomDateRange(false);
    setSelectedBillingPeriod(null);
  }, [values.projectId, billingPeriods.length]);

  // Combined loading state for all filters data
  const isLoadingFiltersData = loadingAllWarehouses || loadingProjectData || loadingProjectsData;

  const warehouses = useMemo(() => {
    if (isWarehouseLocationsReport) {
      return allWarehouses;
    } else if (values.projectId === "all" || values.projectIds === "all") {
      return allWarehouses;
    } else if (
      values.projectIds &&
      Array.isArray(values.projectIds) &&
      values.projectIds.length > 0 &&
      projectsData
    ) {
      return projectsData.warehouses || [];
    } else if (projectData) {
      return projectData.warehouses || [];
    }
    return [];
  }, [
    isWarehouseLocationsReport,
    values.projectId,
    values.projectIds,
    allWarehouses,
    projectsData,
    projectData,
  ]);

  const costCenters = useMemo(() => {
    if (
      values.projectIds &&
      Array.isArray(values.projectIds) &&
      values.projectIds.length > 0 &&
      projectsData
    ) {
      return projectsData.costCenters || [];
    } else if (projectData) {
      return projectData.costCenters || [];
    }
    return [];
  }, [values.projectIds, projectsData, projectData]);

  const sortedWarehouses = [
    { id: "all", name: "Select All", isSelectAll: true },
    ...sortBy(warehouses, (warehouse) => warehouse.name?.toLowerCase() || "").map((warehouse) => ({
      id: warehouse.warehouseId,
      name: warehouse.name,
      isSelectAll: false,
    })),
  ];

  const sortedCostCenters = [
    { id: null, costCenterName: "All Cost Centers" },
    ...sortBy(costCenters, (costCenter) => costCenter.costCenterName?.toLowerCase() || ""),
  ];

  // Auto-load warehouses for warehouse locations report
  useEffect(() => {
    if (isWarehouseLocationsReport && !shouldFetchFiltersData) {
      setShouldFetchFiltersData(true);
    }
  }, [isWarehouseLocationsReport, shouldFetchFiltersData]);

  const selectProject = ({ clientId, projectId }) => {
    if (projectId) {
      setFieldValue("clientId", clientId);
      setFieldValue("projectId", projectId);
      setFieldValue("warehouseId", null);
      setFieldValue("costCenterId", null);
    } else {
      setFieldValue("clientId", clientId);
    }
  };

  const selectProjectOnly = ({ projectId }) => {
    setFieldValue("projectId", projectId);
    setFieldValue("warehouseId", null);
    setFieldValue("costCenterId", null);
  };

  const selectClient = (clientId) => {
    setFieldValue("clientId", clientId);
    // Auto-select "All" in project dropdown when "All" clients is selected
    if (clientId === "null") {
      setFieldValue("projectId", "all");
    }
  };

  const handleProjectIdsChange = (projectIds, selectedOptions) => {
    // Clear warehouse and cost center filters when projects change
    setFieldValue("warehouseId", []);
    setFieldValue("costCenterId", []);

    // Check if all selected projects belong to the same client
    if (selectedOptions && selectedOptions.length > 0) {
      const clientIds = [
        ...new Set(selectedOptions.map((option) => option.clientId).filter(Boolean)),
      ];

      // If all projects belong to exactly one client, set clientId
      if (clientIds.length === 1) {
        setFieldValue("clientId", clientIds[0]);
      } else {
        setFieldValue("clientId", null);
      }
    }
  };

  const hasProjectSelection =
    isOperationalActivityMode ||
    values.projectId === "all" ||
    values.projectId ||
    values.projectIds === "all" ||
    (values.projectIds && Array.isArray(values.projectIds) && values.projectIds.length > 0);

  // For warehouse locations report, we don't need project selection
  const hasWarehouseSelection = Array.isArray(values.warehouseId) && values.warehouseId.length > 0;

  const hasRequiredInventoryAsOfDate = !inventoryAsOfDate || !!values.inventoryAsOfDate;
  const isFormValid = hasRequiredInventoryAsOfDate && (isWarehouseLocationsReport
    ? true // Warehouse locations report is always valid (all warehouses or selected warehouses)
    : simpleValidation
      ? hasProjectSelection
      : name === "Receiving Report"
        ? !!(hasProjectSelection && values.startDate && values.endDate)
        : name === "Receiving Report 2"
          ? !!(
              hasProjectSelection &&
              (values.receivingNotes || (values.startDate && values.endDate))
            )
          : name === "Receiving Report 3"
            ? !!(hasProjectSelection && values.receivingNotes)
            : name === "Billing Report (Prototype)"
              ? !!(hasProjectSelection && values.startDate && values.endDate)
            : true);

  return (
    <Form>
      {description && (
        <Box mb={2}>
          <Typography>{description}</Typography>
        </Box>
      )}
      {isWarehouseLocationsReport ? (
        // Warehouse Locations Export: Show warehouse selection dropdown
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Select Warehouse(s)
          </Typography>
          <Autocomplete
            fullWidth
            multiple
            disableCloseOnSelect={false}
            options={sortedWarehouses}
            value={sortedWarehouses.filter((w) =>
              !w.isSelectAll && Array.isArray(values.warehouseId)
                ? values.warehouseId.includes(w.id)
                : values.warehouseId === w.id
            )}
            getOptionLabel={(option) => option.name || ""}
            onChange={(event, newValues) => {
              // Check if "Select All" was clicked
              const hasSelectAll = newValues.some((v) => v.isSelectAll);
              if (hasSelectAll) {
                // Select all warehouses (excluding the "Select All" option itself)
                const allWarehouseIds = sortedWarehouses
                  .filter((w) => !w.isSelectAll)
                  .map((w) => w.id);
                setFieldValue("warehouseId", allWarehouseIds);
              } else {
                const selectedIds = newValues.map((v) => v.id);
                setFieldValue("warehouseId", selectedIds);
              }
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderOption={(props, option, { selected }) => {
              if (option.isSelectAll) {
                return (
                  <React.Fragment key={option.id}>
                    <li {...props} style={{ fontWeight: 500 }}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                    <Divider />
                  </React.Fragment>
                );
              }
              return (
                <li {...props} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Filter by Warehouse" variant="outlined" size="small" />
            )}
          />
        </Box>
      ) : reportType === "billing" ? (
        // Billing Report (Prototype): single project selector only
        <Box mt={2}>
          <FilterProject projectId={values.projectId} onSelectProject={selectProjectOnly} />
        </Box>
      ) : isMultiProjectBillingReport && isOperationalActivityMode ? (
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            This report outputs customers with inbound, outbound, or internal activity
          </Typography>
        </Box>
      ) : isMultiProjectBillingReport ? (
        warehouseDropdownEnabled ? (
          // Multi-project billing with warehouse dropdown: flat multi-select
          <Box mt={2}>
            <ProjectAutocomplete
              name="projectIds"
              multiple
              fullWidth
              label="Select"
              enableManagedSearch
              onChange={handleProjectIdsChange}
              maxSelection={40}
              allowSelectAll={true}
            />
          </Box>
        ) : (
          // Multi-project billing without warehouse dropdown: nested client-project multi-select
          <Box mt={2}>
            <ProjectAutocomplete
              name="projectIds"
              multiple
              group={true}
              fullWidth
              label="Select"
              enableManagedSearch
              onChange={handleProjectIdsChange}
              maxSelection={40}
              allowSelectAll={true}
            />
          </Box>
        )
      ) : warehouseDropdownEnabled ? (
        isPredefined ? (
          // Predefined reports with warehouse dropdown: use original single-project selector
          <Box mt={2}>
            <FilterProject projectId={values.projectId} onSelectProject={selectProjectOnly} />
          </Box>
        ) : (
          // Non-predefined reports with warehouse dropdown: use ProjectAutocomplete
          <Box mt={2}>
            <ProjectAutocomplete
              name="projectIds"
              multiple
              fullWidth
              label="Select"
              enableManagedSearch
              onChange={handleProjectIdsChange}
              allowSelectAll={!disableProjectSelectAll}
            />
          </Box>
        )
      ) : (
        // No warehouse dropdown: use nested client-project ProjectAutocomplete
        <Box mt={2}>
          <ProjectAutocomplete
            name="projectIds"
            multiple
            group={true}
            fullWidth
            label="Select"
            enableManagedSearch
            onChange={handleProjectIdsChange}
            allowSelectAll={!disableProjectSelectAll}
          />
        </Box>
      )}
      {inventoryAsOfDate && (
        <Box mt={2}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              label="Inventory as of"
              format="MM/DD/YYYY"
              open={isInventoryAsOfDateOpen}
              onOpen={() => setIsInventoryAsOfDateOpen(true)}
              onClose={() => setIsInventoryAsOfDateOpen(false)}
              value={values.inventoryAsOfDate}
              onChange={(newValue) => setFieldValue("inventoryAsOfDate", newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  size: "small",
                  autoComplete: "off",
                  onClick: () => setIsInventoryAsOfDateOpen(true),
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      )}
      {/* Show date filters directly for receiving reports and billing reports */}
      {(showReceivingNotes ||
        name === "Billing Report (Prototype)" ||
        name === "Billing Report (Validation)" ||
        isMultiProjectBillingReport) && (
        <>
          {reportType === "billing" ? (
            // Billing Report (Prototype): Only show date fields after project selection
            values.projectId && values.projectId !== "all" ? (
              billingLoading || hasBillingPeriods ? (
                <>
                  <SelectBillingPeriod
                    billingPeriods={billingPeriods}
                    selectedBillingPeriod={selectedBillingPeriod}
                    setSelectedBillingPeriod={setSelectedBillingPeriod}
                    isCustomDateRange={isCustomDateRange}
                    setIsCustomDateRange={setIsCustomDateRange}
                    billingLoading={billingLoading}
                  />
                  {isCustomDateRange && (
                    <DateFilters
                      title="Custom Date Range"
                      startDate={values.startDate}
                      endDate={values.endDate}
                      onStartDateChange={(newValue) => setFieldValue("startDate", newValue)}
                      onEndDateChange={(newValue) => setFieldValue("endDate", newValue)}
                    />
                  )}
                </>
              ) : (
                <DateFilters
                  title="Date Filters"
                  startDate={values.startDate}
                  endDate={values.endDate}
                  onStartDateChange={(newValue) => setFieldValue("startDate", newValue)}
                  onEndDateChange={(newValue) => setFieldValue("endDate", newValue)}
                />
              )
            ) : null
          ) : (
            // Other reports: Keep existing behavior
            <>
              {billingLoading || hasBillingPeriods ? (
                <>
                  <SelectBillingPeriod
                    billingPeriods={billingPeriods}
                    selectedBillingPeriod={selectedBillingPeriod}
                    setSelectedBillingPeriod={setSelectedBillingPeriod}
                    isCustomDateRange={isCustomDateRange}
                    setIsCustomDateRange={setIsCustomDateRange}
                    billingLoading={billingLoading}
                  />
                  {isCustomDateRange && (
                    <DateFilters
                      title="Custom Date Range"
                      startDate={values.startDate}
                      endDate={values.endDate}
                      onStartDateChange={(newValue) => setFieldValue("startDate", newValue)}
                      onEndDateChange={(newValue) => setFieldValue("endDate", newValue)}
                    />
                  )}
                </>
              ) : (
                <DateFilters
                  title="Date Filters"
                  startDate={values.startDate}
                  endDate={values.endDate}
                  onStartDateChange={(newValue) => setFieldValue("startDate", newValue)}
                  onEndDateChange={(newValue) => setFieldValue("endDate", newValue)}
                />
              )}
            </>
          )}
        </>
      )}
      {/* Show date filters in optional section for other reports */}
      {!showReceivingNotes &&
        name !== "Billing Report (Prototype)" &&
        name !== "Billing Report (Validation)" &&
        !isMultiProjectBillingReport &&
        showDateFilters && (
          <DateFilters
            title="Optional Date Filters"
            startDate={values.startDate}
            endDate={values.endDate}
            onStartDateChange={(newValue) => setFieldValue("startDate", newValue)}
            onEndDateChange={(newValue) => setFieldValue("endDate", newValue)}
          />
        )}
      {showWarehouseFilters && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Optional Warehouse Filters
          </Typography>
          <Autocomplete
            fullWidth
            multiple
            disableCloseOnSelect
            options={sortedWarehouses.filter((w) => w.id !== null)} // Exclude "All Warehouses" option for multi-select
            value={sortedWarehouses.filter((w) =>
              Array.isArray(values.warehouseId)
                ? values.warehouseId.includes(w.id)
                : values.warehouseId === w.id
            )}
            getOptionLabel={(option) => option.name || ""}
            onChange={(event, newValues) => {
              const selectedIds = newValues.map((v) => v.id);
              setFieldValue("warehouseId", selectedIds);
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Warehouses" variant="outlined" size="small" />
            )}
          />
        </Box>
      )}
      {showCostCenterFilters && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Optional Cost Center Filters
          </Typography>
          <Autocomplete
            fullWidth
            multiple
            disableCloseOnSelect
            options={sortedCostCenters.filter((c) => c.id !== null)} // Exclude "All Cost Centers" option for multi-select
            value={sortedCostCenters.filter((c) =>
              Array.isArray(values.costCenterId)
                ? values.costCenterId.includes(c.id)
                : values.costCenterId === c.id
            )}
            getOptionLabel={(option) => option.costCenterName || ""}
            onChange={(event, newValues) => {
              const selectedIds = newValues.map((v) => v.id);
              setFieldValue("costCenterId", selectedIds);
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.costCenterName}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Cost Centers" variant="outlined" size="small" />
            )}
          />
        </Box>
      )}
      {showAssetFieldFilters && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Asset Field Filters
          </Typography>
          <AssetFieldFiltersDisplay />
        </Box>
      )}
      {showReceivingNotes && ["Receiving Report 2", "Receiving Report 3"].includes(name) && (
        <Box mt={1.5}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Filter By Keyword(s)"
            value={values.receivingNotes}
            onChange={(event) => setFieldValue("receivingNotes", event.target.value)}
          />
        </Box>
      )}
      {/* Hide optional filters button for receiving reports, billing reports, and warehouse locations report */}
      {!showReceivingNotes &&
        name !== "Billing Report (Prototype)" &&
        name !== "Billing Report (Validation)" &&
        !hideOptionalFiltersButtonForTemplate &&
        !isWarehouseLocationsReport && (
          <AddOptionalFiltersButtonForReceiving
            warehouses={sortedWarehouses.filter((w) => w.id !== null)}
            costCenters={costCenters}
            reportType={reportType}
            name={name}
            alwaysShowDates={alwaysShowDates}
            onToggleDateFilters={setShowDateFilters}
            onToggleWarehouseFilters={setShowWarehouseFilters}
            onToggleCostCenterFilters={setShowCostCenterFilters}
            onToggleAssetFieldFilters={setShowAssetFieldFilters}
            isLoadingFiltersData={isLoadingFiltersData}
            onFetchFiltersData={() => setShouldFetchFiltersData(true)}
          />
        )}
      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button disabled={!isFormValid || isSubmitting} variant="contained" type="submit">
          {buttonLabel}
        </Button>
      </Box>
    </Form>
  );
}
